import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Container from '../components/ui/Container';
import { useQuiz } from '../hooks/useQuiz';
import Loader from '../components/Loader';
import Error from '../components/Error';
import StartScreen from '../features/quiz/StartScreen';
import Question from '../features/quiz/Question';
import Progress from '../features/quiz/Progress';
import FinishScreen from '../features/quiz/FinishScreen';
import EmailForm from '../features/quiz/EmailForm';

import { startQuiz, nextQuestion, finishQuiz, newAnswer, restartQuiz, collectEmail } from '../features/quiz/quizSlice';
import { QuizPageProps, UserData } from '../types';
import { AppDispatch } from '../store';
import { clearQuizData, saveUserData } from '../utils/localStorage';
import { trackQuizStarted, trackQuizEnded, trackSubmitApplication } from '../utils/pixelTracking';

const QuizContainer = styled(Container)`
  width: 100%;
  max-width: 80rem;
`;

const QuizPage: React.FC<QuizPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    status,
    activeQuestion,
    index,
    numQuestions,
    categoryCounts,
    answer,
    answers,
    questions,
    resultsSubmitted
  } = useQuiz();

  function handleStart(): void {
    dispatch(startQuiz());
    trackQuizStarted();
  }

  function handleNewAnswer(answerIndex: number): void {
    dispatch(newAnswer(answerIndex));

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (index < numQuestions - 1) {
        // Move to next question
        handleNextQuestion();
      } else {
        // This is the last question, finish the quiz
        handleFinish();
      }
    }, 100); // 800ms delay to show selection feedback
  }

  function handleNextQuestion(): void {
    dispatch(nextQuestion());
  }

  function handleFinish(): void {
    dispatch(finishQuiz());
    trackQuizEnded();
  }

  function handleRestart(): void {
    // Clear localStorage data when restarting
    clearQuizData();
    dispatch(restartQuiz());
  }

  function handleEmailSubmit(userData: UserData): void {
    saveUserData(userData);
    dispatch(collectEmail());
    trackSubmitApplication(userData);
  }

  return (
    <QuizContainer>
      {status === 'loading' && <Loader />}
      {status === 'error' && <Error />}
      {status === 'ready' && (
        <StartScreen numQuestions={numQuestions} onStart={handleStart} />
      )}
      {status === 'active' && activeQuestion && (
        <>
          <Progress
            index={index}
            numQuestions={numQuestions}
            answer={answer}
          />
          <Question
            question={activeQuestion}
            onNewAnswer={handleNewAnswer}
            answer={answer}
          />
          {/* NextButton removed - auto-advance after answer selection */}
        </>
      )}
      {status === 'email-collection' && (
        <EmailForm
          onSubmit={handleEmailSubmit}
          onRestart={handleRestart}
          categoryCounts={categoryCounts}
          answers={answers}
          questions={questions}
        />
      )}
      {status === 'finished' && (
        <FinishScreen
          categoryCounts={categoryCounts}
          answers={answers}
          questions={questions}
          resultsSubmitted={resultsSubmitted}
          onRestart={handleRestart}
        />
      )}
    </QuizContainer>
  );
};

export default QuizPage;
